'use client';
import { useEffect, useRef, useState, Suspense, useCallback } from 'react';
import { useEsiteCtx } from 'services/esite/context';
import { GET_B2C_TOKEN_API } from 'constants/endpoints.api';
import { Method } from 'constants/enums';
import { getRelativeURLWithParams } from 'utils/url';
import ErrorBoundaryWrapper from 'components/error-boundary';
import { useParams, usePathname } from 'next/navigation';
import { normalizeCommerceLink, getSeoUrl } from 'utils/links';
import { B2C_LOGIN_URL } from 'constants/links';

interface AiAssistantContainerProps {
  isLoggedIn: boolean;
}

// Helper function to determine if the environment is production
function isProd() {
  if (typeof window === 'undefined') {
    // Server-side: cannot determine hostname, assume not production
    return false;
  }
  const prodHosts = [
    'prodauthparts.cat.com',
    'prodaparts-v9.cat.com',
    'prodbparts-v9.cat.com',
    'parts.cat.com'
  ];
  return prodHosts.includes(window.location.hostname);
}

// Helper function to get the assistant environment
function getAssistantEnv(AssistantEnv: { [key: string]: string }): string {
  return isProd() ? AssistantEnv.PROD : AssistantEnv.DEV;
}

export const AiAssistantContainer = ({
  isLoggedIn
}: AiAssistantContainerProps) => {
  const [token, setToken] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eventRef = useRef<any>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const { storeId } = useEsiteCtx();
  const params = useParams();
  const pathname = usePathname();

  const getB2CToken = useCallback(async () => {
    try {
      const url = getRelativeURLWithParams(GET_B2C_TOKEN_API, { storeId });
      const response = await fetch(url, { method: Method.GET });
      const data = await response.json();
      if (data?.accessToken) {
        const newB2Ctoken = data.accessToken.split(' ')[1];
        setToken(newB2Ctoken);
        containerRef.current?.firstElementChild?.setAttribute(
          'access-token',
          newB2Ctoken
        );
      }
    } catch (error) {
      console.error('Get B2C token failed for AI Assistant:', error);
    }
  }, [storeId]);

  const handleTokenEvent = useCallback(
    (eventType: string, eventData: unknown) => {
      !isProd() && console.log('AI Assistant event:', eventType, eventData);
      switch (eventType) {
        case 'ai-assistant-token-expired':
        case 'ai-assistant-token-pending-expiration':
        case 'ai-assistant-token-invalid': {
          void getB2CToken();
          break;
        }
        case 'ai-assistant-redirection-login': {
          const esiteUrl = getSeoUrl(params);
          const isHomePage = esiteUrl === pathname;
          const loginUrl = normalizeCommerceLink(
            getSeoUrl(params, B2C_LOGIN_URL, {
              pageRedirectUrl: pathname,
              loginFromHomePage: isHomePage
            })
          );
          window.location.href = loginUrl;
          break;
        }
        case 'ai-assistant-invalid-configuration': {
          break;
        }
      }
    },
    [getB2CToken, params, pathname]
  );

  interface AiAssistantEventData {
    type?: string;
    detail?: unknown;
    details?: unknown;
    [key: string]: unknown;
  }

  const processEventData = useCallback(
    (data: AiAssistantEventData) => {
      const eventMappings = [
        {
          property: 'TOKEN_EXPIRED',
          eventType: 'ai-assistant-token-expired'
        },
        {
          property: 'TOKEN_PENDING_EXPIRATION',
          eventType: 'ai-assistant-token-pending-expiration'
        },
        {
          property: 'TOKEN_INVALID',
          eventType: 'ai-assistant-token-invalid'
        },
        {
          property: 'REDIRECTION_LOGIN',
          eventType: 'ai-assistant-redirection-login'
        },
        {
          property: 'INVALID_CONFIGURATION',
          eventType: 'ai-assistant-invalid-configuration'
        }
      ];

      for (const { property, eventType } of eventMappings) {
        if (data[eventType]) {
          handleTokenEvent(eventType, data.details);
          break;
        }
        if (data.type === property) {
          handleTokenEvent(eventType, data.detail);
          break;
        }
      }
    },
    [handleTokenEvent]
  );

  useEffect(() => {
    const setupEventsAndToken = async () => {
      const mod = await import('@cat-ai/assistant-component');
      const { AiAssistantDispatchEvent: Events } = mod;
      eventRef.current = Events;

      const handlePostMessage = (event: MessageEvent) => {
        if (!event.origin.includes('assistant')) return;
        const data = event.data;
        if (data && typeof data === 'object') {
          processEventData(data);
        }
      };

      cleanupRef.current = () => {
        window.removeEventListener('message', handlePostMessage);
      };

      if (isLoggedIn) {
        window.addEventListener('message', handlePostMessage);
        await getB2CToken();
      }
    };

    void setupEventsAndToken();

    return () => {
      if (isLoggedIn && cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [getB2CToken, isLoggedIn, processEventData]);

  useEffect(() => {
    const mountComponent = async () => {
      if (!containerRef.current) return;

      const { AssistantEnv } = await import('@cat-ai/assistant-component');

      const assistantElement = document.createElement('cat-ai-assistant');
      assistantElement.setAttribute(
        'environment',
        getAssistantEnv(AssistantEnv)
      );
      assistantElement.setAttribute(
        'app-context',
        '{ "appName": "parts.cat.com" }'
      );

      if (token) {
        assistantElement.setAttribute('access-token', token);
      }

      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(assistantElement);
    };

    void mountComponent();
  }, [token]);

  return (
    <ErrorBoundaryWrapper fallback={null}>
      <Suspense>
        <div id="cat-ai-assistant-container" ref={containerRef} />
      </Suspense>
    </ErrorBoundaryWrapper>
  );
};
